import { useGetTreatmentPlan } from '@/api/get-treatment-plan';
import { PageContainer } from '@/components/containers/page-container';
import { useEncounter } from '@/contexts/encounter-context';
import { Navigate } from '@/util/react-router-dom';
import { EncounterDiagnosisSection } from '@/components/treatment-plan/encounter-diagnosis-section';
import { TreatmentSection } from '@/components/treatment-plan/treatment-section';
import { EncounterInfoHeaderSection } from '@/components/treatment-plan/encounter-info-header-section';
import { EncounterResolution, GetTreatmentPlanErrorType } from '@/api/api.schemas';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { Button } from '@/components/button';
import styles from './treatment-plan-page.module.css';
import { useTranslation } from '@/contexts/i18n-context';
import { IntakeSection } from '@/components/treatment-plan/intake-section';
import { useMediaQuery } from '@/hooks/use-media-query';
import { SaveAndShareButton } from '@/components/treatment-plan/save-and-share-button';
import { Loader } from '@/components/loaders/loader';
import { TreatmentPlanReadModal } from '@/components/treatment-plan/treatment-plan-read-modal';
import { MarketingSpot } from '@/components/marketing-spot';

const translationBaseNav = 'visit.treatmentPlan.navigationButtons';

export function TreatmentPlanPage() {
  const { encounter } = useEncounter();
  const { data: treatmentPlan, isFetching, error } = useGetTreatmentPlan(encounter.id);
  const isMobile = useMediaQuery('--screen-md');

  if (isFetching) {
    return <Loader type="large" />;
  }

  if (error) {
    if (error.type === GetTreatmentPlanErrorType.NotFound) {
      return <Navigate to="/not-found" />;
    }

    return <Navigate to="/system-error" />;
  }

  if (!treatmentPlan) {
    return <Navigate to="/system-error" />;
  }

  if (treatmentPlan.encounterResolution === EncounterResolution.NotCompleted) {
    return <Navigate to="/" />;
  }

  const encounterHasBeenTreated =
    treatmentPlan.encounterResolution === EncounterResolution.Completed;

  const encounterTriagedOut = treatmentPlan.encounterResolution === EncounterResolution.TriageOut;

  const encounterCancelled =
    treatmentPlan.encounterResolution === EncounterResolution.PatientCancelled;

  const saveAndShareButton =
    encounterHasBeenTreated || encounterTriagedOut ? (
      <SaveAndShareButton
        className={styles.saveAndShareButton}
        encounterId={encounter.id}
        encounterResolution={treatmentPlan.encounterResolution}
        hasReferral={treatmentPlan.hasReferralSpecialist || Boolean(treatmentPlan.referralTextHtml)}
      />
    ) : null;

  return (
    <PageContainer>
      {!isMobile && (
        <div className={styles.navigationContainer}>
          <BackToDashboardButton />
          {saveAndShareButton}
        </div>
      )}
      <GradientFormTitle withParagraphMargins={false}>
        {isMobile && <BackToDashboardButton />}
        <EncounterInfoHeaderSection treatmentPlan={treatmentPlan} />
        {isMobile && saveAndShareButton}
      </GradientFormTitle>
      <EncounterDiagnosisSection treatmentPlan={treatmentPlan} />
      {encounterHasBeenTreated && <TreatmentSection treatmentPlan={treatmentPlan} />}
      <IntakeSection encounterCancelled={encounterCancelled} />
      <MarketingSpot page="treatmentPlan" spot="bottom" className={styles.marketingContent} />
      <TreatmentPlanReadModal treatmentPlan={treatmentPlan} />
    </PageContainer>
  );
}

function BackToDashboardButton() {
  const { t } = useTranslation();

  return (
    <Button variant="link" className={styles.backButton} as="a" to="/" leftIcon="arrow_back">
      {t(`${translationBaseNav}.backToDashboard`)}
    </Button>
  );
}
